import { useEffect, useLayoutEffect, useState } from 'react';
/**
 * Returns true if the viewport is mobile size
 * Returns undefined until the first render occured to prevent hydration errors.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useIsMobile() {
    return useMediaQuery(`(max-width: ${768 - 0.02}px)`);
}
const getMatches = (query) => {
    // Prevents SSR issues
    if (typeof window !== 'undefined') {
        return window.matchMedia(query).matches;
    }
    return false;
};
/**
 * Returns true if the given media query matches.
 * Returns undefined until the first render occured to prevent hydration errors.
 * @param query A media query as string
 * @returns
 */
export function useMediaQuery(query) {
    const [hasMatch, setHasMatches] = useState(getMatches(query));
    const [initialLoad, setInitialLoad] = useState(true);
    const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
    useIsomorphicLayoutEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
            handleChange();
        }
    }, []);
    function handleChange() {
        setHasMatches(getMatches(query));
    }
    useEffect(() => {
        const matchMedia = window.matchMedia(query);
        const isModern = !!matchMedia?.addEventListener;
        handleChange();
        if (isModern) {
            matchMedia.addEventListener('change', handleChange);
        }
        else {
            // Fallback for older browsers
            matchMedia.addListener(handleChange);
        }
        return () => {
            if (isModern) {
                matchMedia.removeEventListener('change', handleChange);
            }
            else {
                // Fallback for older browsers
                matchMedia.removeListener(handleChange);
            }
        };
    }, [query]);
    return initialLoad ? undefined : hasMatch;
}
/**
 * Counts how many of the given media queries apply
 * Returns undefined until the first render occured to prevent hydration errors.
 * @param queries An array of media query strings
 * @returns An array of booleans indicating if the media queries match
 */
export function useMediaQueries(queries) {
    const [hasMatch, setHasMatch] = useState([]);
    const [initialLoad, setInitialLoad] = useState(true);
    const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
    useIsomorphicLayoutEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
            handleChange();
        }
    }, []);
    function handleChange() {
        const results = [...queries];
        setHasMatch(results.map(query => !!getMatches(query)));
    }
    useEffect(() => {
        const matchMedias = [];
        queries.forEach(query => {
            matchMedias.push(window.matchMedia(query));
        });
        const isModern = !!matchMedias[0].addEventListener;
        matchMedias.forEach(matchMedia => {
            if (isModern) {
                matchMedia.addEventListener('change', handleChange);
            }
            else {
                // Fallback for older browsers
                matchMedia.addListener(handleChange);
            }
        });
        return () => {
            matchMedias.forEach(matchMedia => {
                if (isModern) {
                    matchMedia.removeEventListener('change', handleChange);
                }
                else {
                    // Fallback for older browsers
                    matchMedia.removeListener(handleChange);
                }
            });
        };
    }, [queries]);
    return initialLoad ? queries.map(() => undefined) : hasMatch;
}
